import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import { fetchJsonObject } from "@/lib/aws-amplify/storage";

import { PATHS } from "@/routes/paths";

import { QueryKeys } from "@/components/features/faq/model/repository/query";
import type { ArticlesResponse } from "@/components/features/faq/model/repository/query/type";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import type { ParamParseKey } from "react-router";

type FaqType = "host" | "guest";

type LocationState = {
	fileName: string;
	type?: FaqType;
};

type Success = {
	canRender: true;
	hotelID: string;
	fileName: string;
	type: FaqType;
};

type Loading = {
	canRender: false;
	isLoading: true;
};

type Failure = {
	canRender: false;
	isLoading: false;
};

type ReturnType = Success | Loading | Failure;

export const useFaqDetailParams = (): ReturnType => {
	const { hotelID, notionID } = useParams<ParamParseKey<typeof PATHS.FAQ_DETAIL>>();
	const location = useLocation();
	const state = location.state as LocationState | null;
	const navigate = useNavigate();

	const [fileName, setFileName] = useState<string | null>(state?.fileName ?? null);
	const [faqType, setFaqType] = useState<FaqType>(state?.type ?? "host");
	const [isLoading, setIsLoading] = useState<boolean>(!state?.fileName);

	useEffect(() => {
		// state から fileName が取得できている場合はスキップ
		if (state?.fileName) return;

		// notionID がない場合はエラー
		if (!hotelID || !notionID) {
			notifyUserEventInfo({
				title: "無効なURLです",
				description: "",
			});
			navigate(PATHS.TOP);
			return;
		}

		// ホストとゲスト両方の history.json から fileName を検索
		const fetchFileName = async () => {
			try {
				// article_id のハイフンを除去して比較（一覧画面でのID変換と合わせる）
				const normalizeId = (id: string) => id.replace(/-/g, "");

				// ホスト用の history.json から検索
				const hostHistoryResponse = await fetchJsonObject<ArticlesResponse>(QueryKeys.hostArticleList);
				const hostArticle = hostHistoryResponse.articles.find(
					(item) => normalizeId(item.article_id) === notionID,
				);

				if (hostArticle) {
					setFileName(hostArticle.file_name);
					setFaqType("host");
					setIsLoading(false);
					return;
				}

				// ゲスト用の history.json から検索
				const guestHistoryResponse = await fetchJsonObject<ArticlesResponse>(QueryKeys.guestArticleList);
				const guestArticle = guestHistoryResponse.articles.find(
					(item) => normalizeId(item.article_id) === notionID,
				);

				if (guestArticle) {
					setFileName(guestArticle.file_name);
					setFaqType("guest");
					setIsLoading(false);
					return;
				}

				// どちらにも見つからない場合
				throw new Error("Article not found");
			} catch (error) {
				notifyUserEventInfo({
					title: "記事が見つかりませんでした",
					description: "",
					variant: "error",
				});
				navigate(PATHS.TOP);
				setIsLoading(false);
			}
		};

		fetchFileName();
	}, [hotelID, notionID, state?.fileName, navigate]);

	if (!hotelID || !notionID) {
		return { canRender: false, isLoading: false };
	}

	if (isLoading) {
		return { canRender: false, isLoading: true };
	}

	if (!fileName) {
		return { canRender: false, isLoading: false };
	}

	return {
		canRender: true,
		hotelID,
		fileName,
		type: faqType,
	};
};
